import { formatOdometer, formatSekAmount, formatRegNumber } from '../../lib/format.jsx';
import { VEHICLE_RECORD_DEMO as DEMO } from './vehicleRecordDemo';

// The three Klargo "papers" of the vehicle record's Handlingar shelf (insurance policy, inspection
// report, vehicle report), ported from Klargo-Reference's retainedDocuments.ts. They are drawn from
// the deal's car record plus the hard-coded values in vehicleRecordDemo.js; every hard-coded value is
// marked as an example. (The purchase contract is not one of them: it opens the real contract view.)

const fill = (template, values) => Object.entries(values).reduce((text, [k, v]) => text.replace(`{${k}}`, v), template);
const pick = (obj, language) => (language && language.startsWith('en') ? obj.en : obj.sv);

export const DOCUMENT_KIND = { policy: 'policy', inspection: 'inspection', report: 'report' };

// "Bilvision" is named only for values that came from it: the insurance policy is always an example,
// the inspection result is always an example, and the inspection dates/mileage only when the car record has them.
// ctx: { tp, language, dateOf, reg, name, lastInspected, nextBy, mileageKm, retrievedAt, reference, facts, inspectionReal, mileageReal }
export function buildVehicleDocumentPaper(kind, ctx) {
  const { tp, language, dateOf, reg, name, lastInspected, nextBy, mileageKm, retrievedAt, reference, inspectionReal, mileageReal } = ctx;
  const subtitle = `${formatRegNumber(reg)} · ${name}`.trim();
  const footerLine = tp('contract.preview.company');
  const sek = (n) => formatSekAmount(n, language);

  if (kind === DOCUMENT_KIND.policy) {
    const ins = DEMO.insurance;
    return {
      title: tp('ds.document.insurancePolicy'),
      brandLine: tp('vehicle.policyDocument.brandLine'),
      referenceLine: tp('ds.sample.paper'),
      footerLine,
      blocks: [
        { kind: 'title', title: tp('ds.document.insurancePolicy'), subtitle },
        {
          kind: 'meta',
          fields: [
            { label: tp('vehicle.policyDocument.insurer'), value: ins.provider },
            { label: tp('vehicle.policyDocument.broker'), value: ins.broker },
            { label: tp('vehicle.policyDocument.issued'), value: dateOf(ins.documentDate) },
          ],
        },
        {
          kind: 'fields',
          heading: tp('vehicle.policyDocument.policy'),
          fields: [
            { label: tp('vehicle.policyDocument.coverage'), value: pick(ins.coverage, language) },
            { label: tp('ds.insurance.validFrom'), value: dateOf(ins.startDate) },
            { label: tp('vehicle.policyDocument.premiumMonthly'), value: sek(ins.monthlyPremiumSek) },
            { label: tp('vehicle.policyDocument.premiumAnnual'), value: sek(ins.annualPremiumSek) },
            { label: tp('ds.insurance.deductible'), value: sek(ins.deductibleSek) },
            { label: tp('ds.insurance.policyNumber'), value: ins.maskedPolicyNumber },
          ],
        },
        { kind: 'note', paragraphs: [tp('ds.sample.insurance')] },
      ],
    };
  }

  const approved = `${tp(DEMO.inspection.approved ? 'record.report.inspection.approved' : 'record.report.inspection.rejected')} (${tp('ds.sample.label').toLowerCase()})`;
  const odometer = mileageReal ? formatOdometer(mileageKm, language) : '';
  const inspectionFields = [
    { label: tp('record.report.inspection.last'), value: inspectionReal ? dateOf(lastInspected) : '' },
    { label: tp('record.report.inspection.result'), value: approved },
    { label: tp('record.report.inspection.odometer'), value: odometer },
    { label: tp('record.report.inspection.nextBy'), value: inspectionReal ? dateOf(nextBy) : '' },
  ].filter((f) => f.value);

  if (kind === DOCUMENT_KIND.inspection) {
    return {
      title: tp('ds.document.inspectionReport'),
      brandLine: tp('vehicle.inspectionReport.brandLine'),
      referenceLine: inspectionReal ? tp('record.report.sourceLine') : tp('ds.sample.paper'),
      footerLine,
      blocks: [
        { kind: 'title', title: tp('ds.document.inspectionReport'), subtitle },
        {
          kind: 'meta',
          fields: [
            { label: tp('record.report.source'), value: inspectionReal ? 'Bilvision' : tp('ds.sample.label') },
            ...(inspectionReal ? [{ label: tp('vehicle.inspectionReport.inspected'), value: dateOf(lastInspected) }] : []),
          ],
        },
        {
          kind: 'fields',
          heading: tp('ds.document.inspectionDetails'),
          fields: inspectionFields.filter((f) => f.label !== tp('record.report.inspection.last')),
        },
        { kind: 'note', paragraphs: [tp(inspectionReal ? 'ds.sample.inspectionResult' : 'ds.sample.inspection')] },
      ],
    };
  }

  // Fordonsrapport
  const facts = (ctx.facts || []).filter((f) => f.value);
  return {
    title: tp('ds.document.vehicleReport'),
    brandLine: tp('record.report.brandLine'),
    referenceLine: tp('record.report.sourceLine'),
    footerLine,
    blocks: [
      { kind: 'title', title: tp('ds.document.vehicleReport'), subtitle },
      {
        kind: 'meta',
        fields: [
          { label: tp('record.report.source'), value: 'Bilvision' },
          { label: tp('record.report.retrieved'), value: dateOf(retrievedAt) },
          { label: tp('record.report.forDeal'), value: reference },
        ],
      },
      { kind: 'clause', heading: tp('record.report.about'), paragraphs: [tp('record.report.about.body')] },
      { kind: 'clause', heading: tp('record.report.verified'), paragraphs: [tp('record.report.verified.body')] },
      { kind: 'fields', heading: tp('record.report.vehicle'), fields: facts },
      {
        kind: 'fields',
        heading: tp('record.report.inspection'),
        fields: inspectionFields,
      },
      { kind: 'clause', heading: tp('record.report.sellerReported'), paragraphs: [tp('record.report.sellerReported.body')] },
      { kind: 'clause', heading: tp('record.report.important'), paragraphs: [tp('record.report.important.body')] },
      { kind: 'clause', heading: tp('record.report.aboutSource'), paragraphs: [tp('record.report.aboutSource.body')] },
      { kind: 'note', paragraphs: [fill(tp('record.report.note'), { date: dateOf(retrievedAt) })] },
    ],
  };
}
